import React from 'react';
import { Users, ArrowRight, UserPlus, Settings } from 'lucide-react';
import { useGoalContext } from '../hooks/useGoalContext';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';

/**
 * TeamPage - Overview of all team goals where user is a member
 * Displayed in AgendaPage Team tab
 */
const TeamPage: React.FC = () => {
  const [user] = useAuthState(auth);
  const { availableGoals, setActiveGoalId, loading } = useGoalContext(user?.uid);

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 mb-4" style={{ borderColor: '#8DA8A0' }}></div>
          <p className="text-gray-600">Team doelen laden...</p>
        </div>
      </div>
    );
  }

  if (availableGoals.length === 0) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-8 px-4">
        <div className="rounded-full p-5 mb-3" style={{ backgroundColor: 'rgba(141,168,160,0.1)' }}>
          <Users className="w-10 h-10" style={{ color: '#8DA8A0' }} />
        </div>
        <h3 className="text-sm font-semibold mb-1" style={{ color: '#3C4F49' }}>
          Geen team doelen
        </h3>
        <p className="text-xs text-center w-full mb-4" style={{ color: '#5E6E68' }}>
          Je bent nog geen teamlid van een doel. Vraag de eigenaar van een doel om je uit te nodigen.
        </p>
        <div className="rounded-lg p-3 w-full" style={{ backgroundColor: 'rgba(141,168,160,0.06)', border: '1px solid rgba(141,168,160,0.15)' }}>
          <p className="text-xs" style={{ color: '#4F6660' }}>
            💡 <strong>Tip:</strong> Je kunt ook vanuit je eigen persoonlijke doelen teamleden toevoegen door naar de Journey pagina te gaan en de Team tab te openen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold" style={{ color: '#3C4F49' }}>Team Doelen</h2>
          <p className="text-xs" style={{ color: '#5E6E68' }}>
            Doelen waar je als teamlid aan werkt ({availableGoals.length})
          </p>
        </div>
      </div>

      {/* Team Goals Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {availableGoals.map((goal) => (
          <div
            key={goal.goalId}
            className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate mb-2">
                  {goal.goalTitle}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getRoleLabel(goal.role)}
                  </span>
                  {goal.function && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {goal.function}
                    </span>
                  )}
                  {goal.accessLevel === 'agenda-only' && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      Beperkte toegang
                    </span>
                  )}
                </div>
              </div>
              <Users className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
            </div>

            {/* Access Level Info */}
            {goal.accessLevel === 'agenda-only' && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  Je hebt alleen toegang tot de Agenda pagina voor dit doel
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveGoalId(goal.goalId)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
              >
                <span>Bekijk doel</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Over Team Context
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Klik op "Bekijk doel" om naar de context van dat team doel te schakelen</li>
          <li>• In team context zie je alleen de data van dat specifieke doel</li>
          <li>• Je kunt altijd terug naar je eigen omgeving via de context switcher bovenaan</li>
          <li>• Je toegangsniveau bepaalt welke pagina's je kunt zien</li>
        </ul>
      </div>
    </div>
  );
};

function getRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    owner: 'Eigenaar',
    member: 'Teamlid',
    va: 'VA',
    manager: 'Manager',
    assistant: 'Assistent',
  };
  return labels[role] || role;
}

export default TeamPage;
